import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Textarea } from './ui/textarea';
import { Progress } from './ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Package, Plus, Loader2, Download, Trash2, TrendingDown, AlertTriangle, FileSpreadsheet } from 'lucide-react';
import { bienesInversionAPI } from '../api';
import { toast } from '../hooks/use-toast';

const eur = (n) => new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 }).format(Number(n || 0));

const emptyForm = () => ({
  descripcion: '', proveedor_nombre: '', proveedor_nif: '', numero_factura: '',
  fecha_adquisicion: '', fecha_puesta_funcionamiento: '',
  valor_adquisicion: 0, valor_suelo: 0, porcentaje_afectacion: 100,
  cuota_iva_soportada: 0, cuota_iva_deducida: 0,
  prorrata_adquisicion: 100,
  grupo_amortizacion: '', coeficiente: 0, escaso_valor: false,
  amortizacion_acumulada_inicial: 0, notas: '',
});

const BienesInversion = ({ ejercicio }) => {
  const [tabla, setTabla] = useState([]);
  const [bienes, setBienes] = useState([]);
  const [amort, setAmort] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detalle, setDetalle] = useState(null);
  const [bajaOpen, setBajaOpen] = useState(false);
  const [bajaForm, setBajaForm] = useState(null);
  const [candidatos, setCandidatos] = useState([]);
  const [gastoOrigen, setGastoOrigen] = useState('');
  const [cierre, setCierre] = useState(null);
  const [cerrando, setCerrando] = useState(false);
  const [regul, setRegul] = useState(null);
  const [regulProrrata, setRegulProrrata] = useState('');

  const cargarRegularizacion = async () => {
    try {
      const r = await bienesInversionAPI.regularizacionIva(ejercicio, regulProrrata);
      setRegul(r.data);
      if (!r.data.detalle?.length) toast({ title: 'Regularización IVA', description: 'No procede regularización este ejercicio (sin diferencias > 10 puntos)' });
    } catch (e) { toast({ title: 'Error', variant: 'destructive' }); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, a, c, ce] = await Promise.all([
        bienesInversionAPI.getAll(),
        bienesInversionAPI.getAmortizaciones(ejercicio),
        bienesInversionAPI.getCandidatos().catch(() => ({ data: [] })),
        bienesInversionAPI.estadoCierre(ejercicio).catch(() => ({ data: null })),
      ]);
      setBienes(b.data || []);
      setAmort(a.data || null);
      setCandidatos(c.data || []);
      setCierre(ce.data || null);
    } catch (e) {
      toast({ title: 'Error', description: 'No se pudieron cargar los bienes de inversión', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [ejercicio]);

  useEffect(() => { bienesInversionAPI.getTabla().then(r => setTabla(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const grupoSel = tabla.find(g => String(g.grupo) === String(form.grupo_amortizacion));

  const handleGrupoChange = (v) => {
    const g = tabla.find(x => String(x.grupo) === String(v));
    setForm(prev => ({ ...prev, grupo_amortizacion: v, coeficiente: g ? g.coef_lineal_max : prev.coeficiente }));
  };

  const handleGastoOrigen = (gastoId) => {
    setGastoOrigen(gastoId);
    if (gastoId === '__none__' || !gastoId) { setGastoOrigen(''); return; }
    const g = candidatos.find(c => String(c.id) === String(gastoId));
    if (g) {
      setForm(prev => ({
        ...prev,
        descripcion: g.descripcion || prev.descripcion,
        proveedor_nombre: g.proveedor_nombre || '',
        numero_factura: g.numero_factura || '',
        fecha_adquisicion: (g.fecha || '').slice(0, 10),
        fecha_puesta_funcionamiento: (g.fecha || '').slice(0, 10),
        valor_adquisicion: g.importe_base || 0,
        cuota_iva_soportada: g.importe_iva || 0,
        cuota_iva_deducida: g.importe_iva || 0,
        porcentaje_afectacion: g.porcentaje_afectacion || 100,
      }));
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (gastoOrigen) {
        await bienesInversionAPI.crearDesdeGasto(gastoOrigen, {
          grupo_amortizacion: form.grupo_amortizacion ? parseInt(form.grupo_amortizacion) : null,
          coeficiente: parseFloat(form.coeficiente) || 0,
          fecha_puesta_funcionamiento: form.fecha_puesta_funcionamiento || null,
          valor_suelo: parseFloat(form.valor_suelo) || 0,
          escaso_valor: form.escaso_valor,
        });
      } else {
        const data = {
          ...form,
          grupo_amortizacion: form.grupo_amortizacion ? parseInt(form.grupo_amortizacion) : null,
          coeficiente: parseFloat(form.coeficiente) || 0,
          valor_adquisicion: parseFloat(form.valor_adquisicion) || 0,
          valor_suelo: parseFloat(form.valor_suelo) || 0,
          porcentaje_afectacion: parseFloat(form.porcentaje_afectacion) || 100,
          cuota_iva_soportada: parseFloat(form.cuota_iva_soportada) || 0,
          cuota_iva_deducida: parseFloat(form.cuota_iva_deducida) || 0,
          prorrata_adquisicion: parseFloat(form.prorrata_adquisicion) || 100,
          amortizacion_acumulada_inicial: parseFloat(form.amortizacion_acumulada_inicial) || 0,
          fecha_adquisicion: form.fecha_adquisicion || null,
          fecha_puesta_funcionamiento: form.fecha_puesta_funcionamiento || null,
        };
        await bienesInversionAPI.create(data);
      }
      toast({ title: 'Bien de inversión creado' });
      setDialogOpen(false);
      setForm(emptyForm());
      setGastoOrigen('');
      load();
    } catch (err) {
      toast({ title: 'Error', description: err?.response?.data?.detail || 'No se pudo guardar', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleCerrar = async () => {
    if (!window.confirm(`¿Cerrar el ejercicio ${ejercicio}? Las dotaciones de amortización quedarán congeladas.`)) return;
    setCerrando(true);
    try {
      const r = await bienesInversionAPI.cerrarEjercicio(ejercicio);
      toast({ title: `Ejercicio ${ejercicio} cerrado`, description: `${r.data.activos_congelados} activo(s) congelado(s)` });
      load();
    } catch (e) {
      toast({ title: 'Error', description: 'No se pudo cerrar el ejercicio', variant: 'destructive' });
    } finally { setCerrando(false); }
  };

  const openDetalle = async (id) => {
    try {
      const r = await bienesInversionAPI.getById(id);
      setDetalle(r.data);
    } catch (e) { toast({ title: 'Error', variant: 'destructive' }); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este bien de inversión?')) return;
    try { await bienesInversionAPI.delete(id); toast({ title: 'Eliminado' }); load(); }
    catch (e) { toast({ title: 'Error', variant: 'destructive' }); }
  };

  const openBaja = (b) => {
    setBajaForm({ id: b.id, descripcion: b.descripcion, fecha_baja: '', causa_baja: 'venta', importe_transmision: 0, prorrata_venta: 100, prorrata_adquisicion: 100 });
    setBajaOpen(true);
  };

  const handleBaja = async () => {
    try {
      const r = await bienesInversionAPI.baja(bajaForm.id, {
        fecha_baja: bajaForm.fecha_baja,
        causa_baja: bajaForm.causa_baja,
        importe_transmision: parseFloat(bajaForm.importe_transmision) || 0,
        prorrata_venta: parseFloat(bajaForm.prorrata_venta) || 100,
        prorrata_adquisicion: parseFloat(bajaForm.prorrata_adquisicion) || 100,
      });
      const d = r.data;
      toast({ title: 'Baja registrada', description: `VNC: ${eur(d.valor_neto_contable)} · Resultado: ${eur(d.resultado_transmision)}` });
      window.alert(d.aviso);
      setBajaOpen(false);
      load();
    } catch (e) { toast({ title: 'Error', description: e?.response?.data?.detail || 'No se pudo dar de baja', variant: 'destructive' }); }
  };

  const descargar = async () => {
    try {
      const r = await bienesInversionAPI.exportLibro(ejercicio, 'gestoria');
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `libro_bienes_inversion_${ejercicio}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { toast({ title: 'Error al exportar', variant: 'destructive' }); }
  };

  const estadoBadge = (estado) => {
    if (estado === 'baja') return <Badge variant="destructive">Dado de baja</Badge>;
    if (estado === 'totalmente_amortizado') return <Badge className="bg-slate-500">Amortizado</Badge>;
    return <Badge className="bg-emerald-600">En curso</Badge>;
  };

  return (
    <div className="space-y-4" data-testid="bienes-inversion">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Package className="w-5 h-5 text-indigo-600" />
          <div>
            <h3 className="text-base font-semibold text-slate-900">Bienes de inversión y amortizaciones</h3>
            <p className="text-xs text-slate-500">Inventario de activos, cuadro de amortización y libro registro · {ejercicio}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={descargar} data-testid="bien-export-libro">
            <FileSpreadsheet className="w-4 h-4 mr-2" />Libro registro
          </Button>
          <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={() => { setForm(emptyForm()); setGastoOrigen(''); setDialogOpen(true); }} data-testid="bien-nuevo">
            <Plus className="w-4 h-4 mr-2" />Nuevo activo
          </Button>
        </div>
      </div>

      {amort && (
        <Card className="border-l-4 border-l-indigo-500">
          <CardContent className="pt-4 flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="text-xs text-slate-500">Amortizaciones del ejercicio {ejercicio}</p>
              <p className="text-2xl font-bold text-indigo-700" data-testid="bien-amort-total">{eur(amort.total_dotacion)}</p>
            </div>
            <div className="flex items-center gap-3">
              {cierre?.cerrado
                ? <Badge className="bg-slate-700" data-testid="bien-cierre-badge">🔒 Ejercicio {ejercicio} cerrado ({cierre.activos_congelados})</Badge>
                : <Button variant="outline" size="sm" onClick={handleCerrar} disabled={cerrando} data-testid="bien-cerrar-ejercicio">
                    {cerrando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}Cerrar ejercicio {ejercicio}
                  </Button>}
              <p className="text-xs text-slate-400 max-w-[220px]">{amort.detalle?.length || 0} activo(s) con dotación · se integra en el Modelo 130 y el cierre</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Regularización IVA anual (bienes de inversión) */}
      <Card data-testid="bien-regularizacion-card">
        <CardHeader><CardTitle className="text-sm">Regularización anual de IVA (art. 107 LIVA)</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <Label className="text-xs">% Prorrata del ejercicio (opcional)</Label>
              <Input type="number" min="0" max="100" placeholder="usar config" value={regulProrrata}
                onChange={e => setRegulProrrata(e.target.value)} className="w-40" data-testid="regul-prorrata" />
            </div>
            <Button variant="outline" onClick={cargarRegularizacion} data-testid="regul-calcular">Calcular regularización</Button>
            <p className="text-xs text-slate-400 max-w-sm">Deja el campo vacío para usar la prorrata de Configuración fiscal; o introduce un valor para simular.</p>
          </div>
          {regul && (
            <div className="text-sm">
              <p className="mb-2">Prorrata usada: <b>{regul.prorrata_ejercicio}%</b> · Ajuste total: <b className={regul.total_ajuste < 0 ? 'text-red-600' : 'text-emerald-600'}>{eur(regul.total_ajuste)}</b> {regul.total_ajuste < 0 ? '(a ingresar)' : regul.total_ajuste > 0 ? '(mayor deducción)' : ''}</p>
              {regul.detalle?.length > 0 && (
                <table className="w-full text-xs border-t">
                  <thead><tr className="text-left text-slate-500"><th className="py-1">Bien</th><th>Tipo</th><th>Periodo</th><th>Δ prorrata</th><th className="text-right">Ajuste</th></tr></thead>
                  <tbody>
                    {regul.detalle.map((d, i) => (
                      <tr key={i} className="border-t"><td className="py-1">{d.descripcion}</td><td>{d.es_inmueble ? 'Inmueble' : 'Bien'}</td><td>{d.periodo} años</td><td>{d.diferencia_prorrata} pts</td><td className="text-right">{eur(d.ajuste)}</td></tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-indigo-500" /></div>
      ) : (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b">
                <tr className="text-left text-xs uppercase text-slate-500">
                  <th className="px-4 py-3">Descripción</th>
                  <th className="px-4 py-3">Puesta func.</th>
                  <th className="px-4 py-3 text-right">Valor</th>
                  <th className="px-4 py-3">Grupo / %</th>
                  <th className="px-4 py-3 w-40">Amortizado</th>
                  <th className="px-4 py-3 text-right">VNC</th>
                  <th className="px-4 py-3">Estado</th>
                  <th className="px-4 py-3 text-center">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {bienes.length > 0 ? bienes.map(b => (
                  <tr key={b.id} className="border-b hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <button className="text-indigo-600 hover:underline text-left" onClick={() => openDetalle(b.id)} data-testid={`bien-detalle-${b.id}`}>{b.descripcion}</button>
                      {b.es_bien_inversion_iva && <span className="ml-2 text-[10px] text-slate-400">(BI IVA)</span>}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">{b.fecha_puesta_funcionamiento || '-'}</td>
                    <td className="px-4 py-3 text-right">{eur(b.valor_adquisicion)}</td>
                    <td className="px-4 py-3">{b.grupo_amortizacion ? `G${b.grupo_amortizacion} · ${b.coeficiente}%` : '-'}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Progress value={b.progreso || 0} className="h-2" />
                        <span className="text-xs text-slate-500 w-10">{b.progreso || 0}%</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">{eur(b.valor_neto_contable)}</td>
                    <td className="px-4 py-3">{estadoBadge(b.estado)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-1">
                        {b.estado !== 'baja' && (
                          <button className="p-1 hover:bg-slate-200 rounded" title="Dar de baja / transmisión" onClick={() => openBaja(b)} data-testid={`bien-baja-${b.id}`}>
                            <TrendingDown className="w-4 h-4 text-amber-600" />
                          </button>
                        )}
                        <button className="p-1 hover:bg-slate-200 rounded" title="Eliminar" onClick={() => handleDelete(b.id)}>
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </button>
                      </div>
                    </td>
                  </tr>
                )) : (
                  <tr><td colSpan="8" className="px-4 py-10 text-center text-slate-400">
                    <Package className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                    No hay bienes de inversión registrados
                  </td></tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Dialog alta */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nuevo bien de inversión</DialogTitle></DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            {candidatos.length > 0 && (
              <div className="rounded border border-emerald-200 bg-emerald-50/50 p-2">
                <Label className="text-xs text-emerald-700">Heredar de un gasto marcado como bien de inversión</Label>
                <Select value={gastoOrigen || '__none__'} onValueChange={handleGastoOrigen}>
                  <SelectTrigger data-testid="bien-desde-gasto"><SelectValue placeholder="(alta manual)" /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    <SelectItem value="__none__">(alta manual)</SelectItem>
                    {candidatos.map(c => <SelectItem key={c.id} value={String(c.id)}>{c.descripcion} · {eur(c.importe_base)} · {(c.fecha || '').slice(0, 10)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div><Label>Descripción *</Label><Input required value={form.descripcion} onChange={e => setForm({ ...form, descripcion: e.target.value })} data-testid="bien-descripcion" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Proveedor</Label><Input value={form.proveedor_nombre} onChange={e => setForm({ ...form, proveedor_nombre: e.target.value })} /></div>
              <div><Label>NIF proveedor</Label><Input value={form.proveedor_nif} onChange={e => setForm({ ...form, proveedor_nif: e.target.value })} /></div>
              <div><Label>Nº Factura</Label><Input value={form.numero_factura} onChange={e => setForm({ ...form, numero_factura: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Fecha adquisición</Label><Input type="date" value={form.fecha_adquisicion} onChange={e => setForm({ ...form, fecha_adquisicion: e.target.value })} /></div>
              <div><Label>Fecha puesta en funcionamiento *</Label><Input type="date" value={form.fecha_puesta_funcionamiento} onChange={e => setForm({ ...form, fecha_puesta_funcionamiento: e.target.value })} data-testid="bien-puesta" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Valor adquisición €</Label><Input type="number" step="0.01" value={form.valor_adquisicion} onChange={e => setForm({ ...form, valor_adquisicion: e.target.value })} data-testid="bien-valor" /></div>
              <div><Label>Valor suelo € (inmuebles)</Label><Input type="number" step="0.01" value={form.valor_suelo} onChange={e => setForm({ ...form, valor_suelo: e.target.value })} /></div>
              <div><Label>% Afectación</Label><Input type="number" min="0" max="100" value={form.porcentaje_afectacion} onChange={e => setForm({ ...form, porcentaje_afectacion: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <Label>Grupo amortización (tabla simplificada)</Label>
                <Select value={String(form.grupo_amortizacion)} onValueChange={handleGrupoChange}>
                  <SelectTrigger data-testid="bien-grupo"><SelectValue placeholder="Grupo" /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    {tabla.map(g => <SelectItem key={g.grupo} value={String(g.grupo)}>{g.grupo}. {g.nombre} ({g.coef_lineal_min}%–{g.coef_lineal_max}%)</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Coeficiente %</Label>
                <Input type="number" step="0.01" value={form.coeficiente} onChange={e => setForm({ ...form, coeficiente: e.target.value })} data-testid="bien-coef" />
                {grupoSel && <p className="text-[11px] text-slate-400 mt-1">Rango: {grupoSel.coef_lineal_min}%–{grupoSel.coef_lineal_max}%</p>}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>IVA soportado €</Label><Input type="number" step="0.01" value={form.cuota_iva_soportada} onChange={e => setForm({ ...form, cuota_iva_soportada: e.target.value })} /></div>
              <div><Label>IVA deducido €</Label><Input type="number" step="0.01" value={form.cuota_iva_deducida} onChange={e => setForm({ ...form, cuota_iva_deducida: e.target.value })} /></div>
            </div>
            <div><Label>% Prorrata en la adquisición (IVA)</Label><Input type="number" min="0" max="100" value={form.prorrata_adquisicion} onChange={e => setForm({ ...form, prorrata_adquisicion: e.target.value })} data-testid="bien-prorrata-adq" /><p className="text-[11px] text-slate-400 mt-1">Se usa para la regularización anual de IVA (inmuebles 10 años / resto 5 años).</p></div>
            <div className="grid grid-cols-2 gap-3 items-center">
              <label className="flex items-center justify-between text-sm border rounded px-3 py-2">
                <span>Escaso valor (≤300€, amortiza 100% este año)</span>
                <Switch checked={form.escaso_valor} onCheckedChange={v => setForm({ ...form, escaso_valor: v })} data-testid="bien-escaso" />
              </label>
              <div><Label>Amort. acumulada inicial € (histórico)</Label><Input type="number" step="0.01" value={form.amortizacion_acumulada_inicial} onChange={e => setForm({ ...form, amortizacion_acumulada_inicial: e.target.value })} /></div>
            </div>
            <div><Label>Notas</Label><Textarea value={form.notas} onChange={e => setForm({ ...form, notas: e.target.value })} /></div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" disabled={saving} data-testid="bien-guardar">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Guardar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog detalle / cuadro */}
      <Dialog open={!!detalle} onOpenChange={(o) => !o && setDetalle(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{detalle?.descripcion}</DialogTitle></DialogHeader>
          {detalle && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>Valor adquisición: <b>{eur(detalle.valor_adquisicion)}</b></div>
                <div>Base amortizable: <b>{eur(detalle.base_amortizable)}</b></div>
                <div>Grupo: <b>{detalle.grupo_amortizacion || '-'}</b> · Coef: <b>{detalle.coeficiente}%</b></div>
                <div>% Afectación: <b>{detalle.porcentaje_afectacion}%</b></div>
                <div>VNC actual: <b>{eur(detalle.valor_neto_contable)}</b></div>
                <div>BI a efectos IVA: <b>{detalle.es_bien_inversion_iva ? 'Sí' : 'No'}</b></div>
              </div>
              <Card>
                <CardHeader><CardTitle className="text-sm">Cuadro de amortización</CardTitle></CardHeader>
                <CardContent className="p-0 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b"><tr className="text-xs uppercase text-slate-500">
                      <th className="px-3 py-2 text-left">Ejercicio</th><th className="px-3 py-2 text-right">Días</th>
                      <th className="px-3 py-2 text-right">Dotación</th><th className="px-3 py-2 text-right">Acumulada</th><th className="px-3 py-2 text-right">Pendiente</th>
                    </tr></thead>
                    <tbody>
                      {(detalle.cuadro_amortizacion || []).map(f => (
                        <tr key={f.ejercicio} className="border-b">
                          <td className="px-3 py-2">{f.ejercicio}</td>
                          <td className="px-3 py-2 text-right">{f.dias}</td>
                          <td className="px-3 py-2 text-right">{eur(f.dotacion)}</td>
                          <td className="px-3 py-2 text-right">{eur(f.acumulada)}</td>
                          <td className="px-3 py-2 text-right">{eur(f.pendiente)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog baja */}
      <Dialog open={bajaOpen} onOpenChange={setBajaOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Baja / transmisión</DialogTitle></DialogHeader>
          {bajaForm && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">{bajaForm.descripcion}</p>
              <div><Label>Fecha de baja *</Label><Input type="date" value={bajaForm.fecha_baja} onChange={e => setBajaForm({ ...bajaForm, fecha_baja: e.target.value })} data-testid="baja-fecha" /></div>
              <div>
                <Label>Causa</Label>
                <Select value={bajaForm.causa_baja} onValueChange={v => setBajaForm({ ...bajaForm, causa_baja: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="venta">Venta / transmisión</SelectItem>
                    <SelectItem value="perdida">Pérdida / siniestro</SelectItem>
                    <SelectItem value="desafectacion">Desafectación</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Importe transmisión €</Label><Input type="number" step="0.01" value={bajaForm.importe_transmision} onChange={e => setBajaForm({ ...bajaForm, importe_transmision: e.target.value })} /></div>
              <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>La ganancia/pérdida por transmisión tributa como ganancia patrimonial (base del ahorro), NO como rendimiento de la actividad.</span>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setBajaOpen(false)}>Cancelar</Button>
                <Button className="bg-amber-600 hover:bg-amber-700" onClick={handleBaja} disabled={!bajaForm.fecha_baja} data-testid="baja-confirmar">Registrar baja</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BienesInversion;
